/**
 * `/settings/billing` — credits-aware Billing & Budgets page (M1.8).
 *
 * Layout (top → bottom):
 *   1. Subscription card — current plan + Stripe Customer Portal link.
 *   2. Plans card — upgrade buttons. Plans are described in credits, not $.
 *   3. Credits this period — allowance bar + wallet balance.
 *   4. Top-up packs — purchase buttons → Stripe Checkout.
 *   5. Auto-refill — opt-in toggle + pack/threshold config.
 *   6. Multipliers reference — small table mapping models to credit burn.
 *   7. Budget limits — legacy $-based caps; kept as a backstop.
 *
 * Owner/admin only for the write paths (checkout, top-up, auto-refill).
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { useMe } from "@/hooks/use-me";
import {
  useBudget,
  useBudgetUsage,
  useUpdateBudget,
  useSubscription,
  useCreateCheckout,
  useBillingPortal,
  useWallet,
  useCreateTopUp,
  useUpdateAutoRefill,
} from "@/hooks/use-billing";
import { MODELS, type ModelEntry, type TopUpPackId } from "@simpliwork/api-schema";

const WRITE_ROLES = new Set(["owner", "admin"]);

function formatMicros(micros: string | null): string {
  if (micros === null) return "Unlimited";
  const dollars = Number(BigInt(micros)) / 1_000_000;
  return `$${dollars.toFixed(2)}`;
}

function formatPct(used: string, cap: string | null): string {
  if (cap === null) return "N/A";
  const pct = (Number(BigInt(used)) / Number(BigInt(cap))) * 100;
  return `${pct.toFixed(1)}%`;
}

function microsToDisplayDollars(micros: string | null): string {
  if (micros === null) return "";
  return (Number(BigInt(micros)) / 1_000_000).toFixed(2);
}

function dollarsToBigIntString(dollars: string): string | null {
  const parsed = parseFloat(dollars);
  if (!dollars.trim() || Number.isNaN(parsed) || parsed < 0) return null;
  return String(Math.round(parsed * 1_000_000));
}

/** Compact credit count: `2,000,000` → `2M`. */
function formatCredits(credits: string | null): string {
  if (credits === null) return "Unlimited";
  const n = Number(BigInt(credits));
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: "Active", color: "text-green-700 bg-green-100" },
  trialing: { label: "Trial", color: "text-blue-700 bg-blue-100" },
  past_due: { label: "Past due", color: "text-yellow-700 bg-yellow-100" },
  canceled: { label: "Canceled", color: "text-red-700 bg-red-100" },
  unpaid: { label: "Unpaid", color: "text-red-700 bg-red-100" },
  none: { label: "No subscription", color: "text-gray-500 bg-gray-100" },
};

/** Subscription states that allow credit consumption (M1 paywall). */
const ENTITLED_STATUSES = new Set(["active", "trialing"]);

/**
 * Plan tiles — credits-first messaging (M1.8). Replaces the earlier
 * "50k tokens / 500k tokens / metered" copy with the locked v1 credit
 * allowances per `docs/MONETIZATION_ROADMAP.md`.
 */
const PLAN_CONFIG = [
  {
    id: "starter" as const,
    label: "Starter",
    price: "$99/mo",
    features: ["2M credits/mo", "3 agents", "250 leads", "20 posts/mo"],
  },
  {
    id: "growth" as const,
    label: "Growth",
    price: "$299/mo",
    features: ["8M credits/mo", "10 agents", "1,000 leads + sequences", "100 posts/mo"],
  },
  {
    id: "scale" as const,
    label: "Scale",
    price: "$799/mo",
    features: [
      "25M credits/mo",
      "Unlimited agents",
      "Unlimited leads + inbox",
      "Unlimited posts",
    ],
  },
];

interface TopUpPackTile {
  id: TopUpPackId;
  label: string;
  price: string;
  credits: string;
  bonus: string;
}

const TOP_UP_TILES: TopUpPackTile[] = [
  { id: "boost", label: "Boost", price: "$25", credits: "500K", bonus: "" },
  { id: "pro", label: "Pro", price: "$100", credits: "2.2M", bonus: "+10% bonus" },
  { id: "power", label: "Power", price: "$500", credits: "12M", bonus: "+20% bonus" },
  { id: "mega", label: "Mega", price: "$2,000", credits: "50M", bonus: "+25% bonus" },
];

export function SettingsBillingPage() {
  const me = useMe();
  const budget = useBudget();
  const usage = useBudgetUsage();
  const update = useUpdateBudget();
  const subscription = useSubscription();
  const checkout = useCreateCheckout();
  const portal = useBillingPortal();
  const wallet = useWallet();
  const topUp = useCreateTopUp();
  const updateAutoRefill = useUpdateAutoRefill();
  const { toast: showToast } = useToast();

  const canWrite = WRITE_ROLES.has(me.data?.role ?? "");

  const [monthlyInput, setMonthlyInput] = useState<string>("");
  const [perRunInput, setPerRunInput] = useState<string>("");
  const [hardStop, setHardStop] = useState<boolean>(false);
  const [editing, setEditing] = useState(false);

  // Auto-refill local state (mirrors wallet response; persists on save).
  const [autoRefillEnabled, setAutoRefillEnabled] = useState(false);
  const [autoRefillPack, setAutoRefillPack] = useState<TopUpPackId>("boost");
  const [autoRefillThreshold, setAutoRefillThreshold] = useState("500000");

  useEffect(() => {
    if (wallet.data) {
      setAutoRefillEnabled(wallet.data.auto_refill_enabled);
      if (wallet.data.auto_refill_pack_id) {
        setAutoRefillPack(wallet.data.auto_refill_pack_id);
      }
      if (wallet.data.auto_refill_threshold_credits) {
        setAutoRefillThreshold(wallet.data.auto_refill_threshold_credits);
      }
    }
  }, [wallet.data]);

  function startEdit() {
    setMonthlyInput(microsToDisplayDollars(budget.data?.monthly_cap_micros ?? null));
    setPerRunInput(microsToDisplayDollars(budget.data?.per_run_cap_micros ?? null));
    setHardStop(budget.data?.hard_stop ?? false);
    setEditing(true);
  }

  async function handleSave() {
    const monthly = dollarsToBigIntString(monthlyInput);
    const perRun = dollarsToBigIntString(perRunInput);
    const body: Record<string, unknown> = {};
    if (monthly !== undefined) body.monthly_cap_micros = monthly;
    if (perRun !== undefined) body.per_run_cap_micros = perRun;
    body.hard_stop = hardStop;

    try {
      await update.mutateAsync(body as Parameters<typeof update.mutateAsync>[0]);
      showToast({ title: "Budget saved", variant: "success" });
      setEditing(false);
    } catch {
      showToast({ title: "Could not save budget", variant: "error" });
    }
  }

  async function handleSaveAutoRefill() {
    try {
      await updateAutoRefill.mutateAsync({
        auto_refill_enabled: autoRefillEnabled,
        auto_refill_pack_id: autoRefillEnabled ? autoRefillPack : null,
        auto_refill_threshold_credits: autoRefillEnabled ? autoRefillThreshold : null,
      });
      showToast({ title: "Auto-refill saved", variant: "success" });
    } catch (err) {
      const description = err instanceof Error ? err.message : null;
      showToast({
        title: "Could not save auto-refill",
        ...(description ? { description } : {}),
        variant: "error",
      });
    }
  }

  function handleTopUpClick(packId: TopUpPackId) {
    topUp.mutate({
      pack: packId,
      success_url: `${window.location.origin}/settings/billing?topup=success`,
      cancel_url: `${window.location.origin}/settings/billing`,
    });
  }

  const currentPlan = subscription.data?.plan ?? "starter";
  const subStatus = subscription.data?.status ?? "none";
  const statusInfo =
    STATUS_LABELS[subStatus] ?? STATUS_LABELS.none ?? { label: subStatus, color: "text-gray-500 bg-gray-100" };
  const periodEnd = subscription.data?.current_period_end
    ? new Date(subscription.data.current_period_end).toLocaleDateString()
    : null;

  const isLoading = budget.isPending || usage.isPending;

  // Credits bar percentage.
  const allowanceUsed = wallet.data ? BigInt(wallet.data.allowance_used_credits) : 0n;
  const allowanceTotal = wallet.data?.allowance_total_credits
    ? BigInt(wallet.data.allowance_total_credits)
    : null;
  const allowancePct =
    allowanceTotal && allowanceTotal > 0n
      ? Math.min((Number(allowanceUsed) / Number(allowanceTotal)) * 100, 100)
      : 0;

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Settings</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Billing &amp; Budgets</h1>
        <p className="text-sm text-muted-foreground">
          Manage your subscription, top up credits, and set spending guardrails.
        </p>
      </header>

      {/* Subscription required prompt — credits don't flow without an
          active subscription per the M1 paywall (`tenants.status` !== 'active'/'trialing'). */}
      {!subscription.isLoading && !ENTITLED_STATUSES.has(subStatus) && (
        <div
          role="status"
          className="rounded-lg border border-primary bg-primary/5 px-4 py-3 text-sm"
        >
          <p className="font-semibold">Subscribe to start running agents</p>
          <p className="mt-1 text-muted-foreground">
            An active Relay subscription is required to dispatch agent runs. Pick a plan below to
            unlock 2M-25M credits per month + access to Anthropic and OpenAI models.
          </p>
        </div>
      )}

      {/* Current subscription */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Subscription</CardTitle>
            {subscription.data?.stripe_customer_id && canWrite && (
              <Button
                variant="outline"
                size="sm"
                disabled={portal.isPending}
                onClick={() => portal.mutate()}
              >
                {portal.isPending ? "Opening…" : "Manage billing"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {subscription.isLoading ? (
            <Skeleton className="h-16 w-full rounded-md" />
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold capitalize">{currentPlan}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>
                {periodEnd && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {subscription.data?.cancel_at_period_end ? "Cancels" : "Renews"} {periodEnd}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan upgrade */}
      {canWrite && (
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Plans</CardTitle>
            <CardDescription>Upgrade or change your plan.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {PLAN_CONFIG.map((plan) => {
                const isCurrent = currentPlan === plan.id && subStatus === "active";
                return (
                  <div
                    key={plan.id}
                    className={`rounded-lg border p-4 flex flex-col gap-2 ${
                      isCurrent ? "border-primary bg-accent/30" : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-semibold text-sm">{plan.label}</span>
                      <span className="text-xs text-muted-foreground">{plan.price}</span>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {plan.features.map((f) => (
                        <li key={f}>· {f}</li>
                      ))}
                    </ul>
                    <Button
                      size="sm"
                      variant={isCurrent ? "outline" : "default"}
                      className="mt-1"
                      disabled={isCurrent || checkout.isPending}
                      onClick={() => {
                        if (!isCurrent) {
                          checkout.mutate({
                            plan: plan.id,
                            success_url: `${window.location.origin}/settings/billing?success=1`,
                            cancel_url: `${window.location.origin}/settings/billing`,
                          });
                        }
                      }}
                    >
                      {isCurrent ? "Current plan" : `Upgrade to ${plan.label}`}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credits this period (M1.8) */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="text-base">Credits this period</CardTitle>
          <CardDescription>
            {wallet.data
              ? `Period: ${new Date(wallet.data.period_start).toLocaleDateString()} – ${new Date(wallet.data.period_end).toLocaleDateString()}`
              : "Loading…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {wallet.isPending ? (
            <Skeleton className="h-20 w-full rounded-md" />
          ) : wallet.data ? (
            <>
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">
                    {formatCredits(wallet.data.allowance_remaining_credits)}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    of {formatCredits(wallet.data.allowance_total_credits)} plan allowance
                  </span>
                </div>
                {wallet.data.allowance_total_credits && (
                  <div className="mt-2 w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${allowancePct}%` }}
                    />
                  </div>
                )}
              </div>
              <div className="border-t pt-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Wallet balance (top-ups, never expire)
                </p>
                <p className="text-2xl font-bold mt-1">
                  {formatCredits(wallet.data.balance_credits)}{" "}
                  <span className="text-sm text-muted-foreground font-normal">credits</span>
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Runs draw from your monthly allowance first, then your wallet.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Wallet unavailable.</p>
          )}
        </CardContent>
      </Card>

      {/* Top-up packs (M1.5) */}
      {canWrite && (
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top up your wallet</CardTitle>
            <CardDescription>
              One-shot purchases that never expire. Credits stack on top of your monthly allowance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {TOP_UP_TILES.map((tile) => (
                <div key={tile.id} className="rounded-lg border border-gray-200 p-3 flex flex-col gap-2">
                  <div className="flex items-baseline justify-between">
                    <span className="font-semibold text-sm">{tile.label}</span>
                    <span className="text-xs text-muted-foreground">{tile.price}</span>
                  </div>
                  <div className="text-xl font-bold">{tile.credits}</div>
                  {tile.bonus && (
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700">
                      {tile.bonus}
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={topUp.isPending}
                    onClick={() => handleTopUpClick(tile.id)}
                  >
                    {topUp.isPending ? "Opening…" : "Buy"}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auto-refill (M1.6) */}
      {canWrite && wallet.data && (
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Auto-refill</CardTitle>
            <CardDescription>
              Optional. Charges your saved card for a top-up pack when your wallet drops below the
              threshold. Otherwise out-of-credit runs hard-stop.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={autoRefillEnabled}
                onChange={(e) => setAutoRefillEnabled(e.target.checked)}
              />
              <span className="text-sm">Enable auto-refill</span>
            </label>

            {autoRefillEnabled && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="auto-refill-pack">Pack to charge</Label>
                  <select
                    id="auto-refill-pack"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={autoRefillPack}
                    onChange={(e) => setAutoRefillPack(e.target.value as TopUpPackId)}
                  >
                    {TOP_UP_TILES.map((tile) => (
                      <option key={tile.id} value={tile.id}>
                        {tile.label} — {tile.price} ({tile.credits})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="auto-refill-threshold">Threshold (credits)</Label>
                  <Input
                    id="auto-refill-threshold"
                    type="number"
                    min="1"
                    step="1"
                    value={autoRefillThreshold}
                    onChange={(e) => setAutoRefillThreshold(e.target.value)}
                  />
                </div>
              </div>
            )}

            <Button
              size="sm"
              onClick={handleSaveAutoRefill}
              disabled={updateAutoRefill.isPending}
            >
              {updateAutoRefill.isPending ? "Saving…" : "Save auto-refill"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Multipliers reference (M1.8) */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Model burn rates</CardTitle>
          <CardDescription>
            Each model burns credits at a different rate. 1× is the reference rate (Sonnet 4.6 / GPT-5.4).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="text-left font-normal pb-2">Model</th>
                <th className="text-left font-normal pb-2">Provider</th>
                <th className="text-right font-normal pb-2">Burn rate</th>
              </tr>
            </thead>
            <tbody>
              {MODELS.map((m: ModelEntry) => (
                <tr key={m.modelId} className="border-t border-gray-100">
                  <td className="py-1.5">{m.displayName}</td>
                  <td className="py-1.5 capitalize text-muted-foreground">{m.provider}</td>
                  <td className="py-1.5 text-right font-mono">{m.multiplier}×</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Budget limits — legacy $-cap backstop (M3.3) */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="text-base">Spending guardrails</CardTitle>
          <CardDescription>
            Optional dollar caps that stop runs before they incur cost. Independent of the credit
            allowance — useful as a safety net during heavy automation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="monthly-cap">Monthly cap (USD)</Label>
                  <Input
                    id="monthly-cap"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Unlimited"
                    value={monthlyInput}
                    onChange={(e) => setMonthlyInput(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="per-run-cap">Per-run cap (USD)</Label>
                  <Input
                    id="per-run-cap"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Unlimited"
                    value={perRunInput}
                    onChange={(e) => setPerRunInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="hard-stop"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={hardStop}
                  onChange={(e) => setHardStop(e.target.checked)}
                />
                <Label htmlFor="hard-stop" className="cursor-pointer">
                  Hard stop — cancel runs that exceed the per-run cap
                </Label>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={update.isPending}>
                  {update.isPending ? "Saving…" : "Save"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Monthly cap</p>
                  <p className="font-medium">{formatMicros(budget.data?.monthly_cap_micros ?? null)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Per-run cap</p>
                  <p className="font-medium">{formatMicros(budget.data?.per_run_cap_micros ?? null)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Hard stop</p>
                  <p className="font-medium">{budget.data?.hard_stop ? "Enabled" : "Disabled"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Used this period</p>
                  <p className="font-medium">
                    {usage.data
                      ? `${formatMicros(usage.data.used_micros)} (${formatPct(usage.data.used_micros, usage.data.cap_micros)})`
                      : "—"}
                  </p>
                </div>
              </div>
              {canWrite && (
                <Button variant="outline" size="sm" onClick={startEdit}>
                  Edit limits
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
